import type { WorkPhase } from "@myapp/contract";
import { Button } from "../../../shared/ui";

type Props = {
  /** From deriveWorkPhase — decides which button, if any, is offered. */
  phase: WorkPhase | null;
  onFollowUp: () => void;
  onReschedule: () => void;
  /**
   * 'header' sits inline in `PageLayout`'s button row — plain `Button`, no wrapper of its own.
   * 'bar' sits in the mobile bottom bar, sharing one row with the primary phase button — the
   * compact, coloured, half-width variant.
   */
  appearance: "header" | "bar";
};

/**
 * The one place "Umplanen" (phase === null) and "Folgetermin erforderlich" (phase === 'ARBEIT')
 * are decided — desktop and mobile used to each carry their own copy of this branching with
 * slightly different markup, which let them drift apart silently.
 *
 * Returns a Fragment on purpose: the desktop caller needs these as direct children of
 * `PageLayout`'s `styles.buttonRow` flex row, and the mobile caller needs it as a direct child of
 * its own `flex` row alongside the primary button — either way a wrapper element here would shift
 * the row's gap/alignment. `flex-1 min-w-0` on the bar variant is what lets it share that row
 * evenly; the primary button is a lone `flex-1` item when neither branch here renders (ANFAHRT /
 * ABFAHRT never pair with a secondary button), so it fills the row on its own with no extra
 * branching needed.
 */
export const AppointmentSecondaryActions = ({ phase, onFollowUp, onReschedule, appearance }: Props) => (
  <>
    {phase === null && (
      <Button
        variant="surface"
        size={appearance === "bar" ? "sm" : undefined}
        className={appearance === "bar" ? "flex-1 min-w-0 border-2 border-[#124093] !text-[#124093] flex items-center justify-center gap-1.5" : undefined}
        onClick={onReschedule}
      >
        Umplanen
      </Button>
    )}
    {phase === "ARBEIT" && (
      <Button
        variant="surface"
        size={appearance === "bar" ? "sm" : undefined}
        className={appearance === "bar" ? "flex-1 min-w-0 border-2 border-yellow-500 !text-yellow-600 flex items-center justify-center gap-1.5" : undefined}
        onClick={onFollowUp}
      >
        {/* Shortened for the bar's half-width column — "Folgetermin erforderlich" wrapped there. */}
        {appearance === "bar" ? "Folgetermin" : "Folgetermin erforderlich"}
      </Button>
    )}
  </>
);
